/**
 * @owner @Appboy/messaging-experience
 */
import { pushTypes } from "@lib/campaignMessageTypeConstants";
import type { SelectableMessageType } from "@lib/campaignMessageTypeConstants";
import sharedConstants from "@lib/shared_constants";

import type { FeatureOn } from "./types";

const { CAMPAIGN_MESSAGE_TYPES } = sharedConstants;

/**
 * Canvas Message Prioritization allowlist from the MX empty-state Figma.
 * Anything not in this set — including banners, Content Cards, and in-app
 * messages — sends without Message Prioritization. Kakao is on this set but
 * additionally requires `message_prioritization_kakao` (see below): send-time
 * frequency capping and prioritization only treat Kakao as eligible behind
 * that flipper, so the UI must agree or it lets marketers configure retry
 * windows and categories the backend will silently ignore.
 */
const messagePrioritizationEligibleChannels = new Set<SelectableMessageType>([
  CAMPAIGN_MESSAGE_TYPES.EMAIL,
  CAMPAIGN_MESSAGE_TYPES.KAKAO,
  CAMPAIGN_MESSAGE_TYPES.LINE,
  CAMPAIGN_MESSAGE_TYPES.SMS,
  CAMPAIGN_MESSAGE_TYPES.WEBHOOK,
  CAMPAIGN_MESSAGE_TYPES.WHATS_APP,
  CAMPAIGN_MESSAGE_TYPES.PUSH,
  ...pushTypes,
]);

/**
 * Whether a single channel can receive Message Prioritization delivery settings.
 *
 * @param channel - A selectable Canvas/campaign message type.
 * @param featureOn - a `featureOn` checker (`useFeatureOn()` or `@lib/featureFlipper`)
 * @returns True when the channel is on the Message Prioritization allowlist.
 */
export const isMessagePrioritizationEligibleChannel = (
  channel: SelectableMessageType,
  featureOn: FeatureOn
): boolean => {
  if (!messagePrioritizationEligibleChannels.has(channel)) {
    return false;
  }
  if (channel === CAMPAIGN_MESSAGE_TYPES.KAKAO) {
    return featureOn("message_prioritization_kakao");
  }
  return true;
};
